'use client';

import { Popover } from '@base-ui/react/popover';
import { css } from '@repo/pandacss/styled-system/css';
import { DisclosureTooltip } from '@repo/ui-v2/DisclosureTooltip';
import { useMemo } from 'react';
import { MdHelpOutline } from 'react-icons/md';
import { singleStarLayoutTooltipClassName } from '../disclosureTooltipPopover';
import { StarIcon } from './components/StarIcon';

type Props = {
  starRating?: number;
  starRatingTooltip?: string;
  reviewLink?: string;
  displayLabel?: boolean;
};

export const DEFAULT_RATING_TOOLTIP = `NerdWallet's ratings are determined by our editorial team. The scoring formulas take into account multiple data points for each financial product and service.`;

export const SingleStarLayout = ({
  displayLabel = false,
  starRating,
  starRatingTooltip = DEFAULT_RATING_TOOLTIP,
  reviewLink,
}: Props) => {
  const handler = useMemo(() => Popover.createHandle<unknown>(), []);

  if (!starRating)
    return (
      <span
        className={css({
          display: 'block',
          color: 'neutral.dark',
          textStyle: 'textSmall',
        })}
      >
        <StarIcon color="#D8D9DA" iconSize={20} className={css({ verticalAlign: 'bottom' })} /> Not
        yet rated
      </span>
    );

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        columnGap: '1',
        rowGap: '1',
      })}
    >
      {displayLabel && (
        <span
          className={css({
            textStyle: 'textSmall',
            color: 'neutral.dark',
          })}
        >
          NerdWallet rating
        </span>
      )}
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          columnGap: '0.5',
          rowGap: '0.5',
        })}
      >
        <span
          className={css({
            display: 'flex',
            alignItems: 'center',
            columnGap: '0.5',
            rowGap: '0.5',
            whiteSpace: 'nowrap',
          })}
        >
          <StarIcon color="#008254" iconSize={20} className={css({ marginBottom: '0.5' })} />
          {reviewLink && starRating ? (
            <a
              href={reviewLink}
              target="_blank"
              rel="noopener noreferrer"
              className={css({
                textStyle: { base: 'bodyLarge', md: 'bodyExtraLarge' },
                color: 'blue.main',
                textDecorationLine: 'none',
                _hover: {
                  textDecorationLine: 'underline',
                },
              })}
            >
              {starRating?.toFixed(1)}
            </a>
          ) : (
            starRating?.toFixed(1)
          )}
          {starRatingTooltip && (
            <>
              <Popover.Trigger
                handle={handler}
                className={css({
                  borderTopStyle: 'none',
                  borderRightStyle: 'none',
                  borderBottomStyle: 'none',
                  borderLeftStyle: 'none',
                  backgroundColor: 'transparent',
                  paddingTop: '0',
                  paddingRight: '0',
                  paddingBottom: '0',
                  paddingLeft: '0',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: 'neutral.dark',
                  fontSize: 'md',
                  lineHeight: 'xs',
                })}
                aria-label="NerdWallet rating info"
              >
                <MdHelpOutline className={css({ color: 'neutral.dark' })} />
              </Popover.Trigger>
              <DisclosureTooltip
                handler={handler}
                tooltipClassName={singleStarLayoutTooltipClassName}
                tooltipStyleOverrides={{
                  lineHeight: 1,
                  maxWidth: '265px',
                }}
              >
                <span
                  className={css({
                    textStyle: 'textSmall',
                    color: 'neutral.darkest',
                  })}
                >
                  {starRatingTooltip}
                </span>
              </DisclosureTooltip>
            </>
          )}
        </span>
      </div>
    </div>
  );
};
